import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import { type PaginationParams } from '@/types/params'
import { type UserProfile } from '@/types/user-profile'
import { type UserStatus } from '@/types/user-status'
import { getUserProfiles } from '@/utils/get-user-profiles'
import { requiredString, userProfile, userStatus } from '@/utils/zod'

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    email: requiredString(),
    phone_number: z.string().optional(),
    profiles: z.array(userProfile),
    is_superuser: z.boolean(),
    is_generator_admin: z.boolean(),
    is_support_staff: z.boolean(),
    status: userStatus.optional()
  })
)

export type User = {
  id: string
  name: string
  email: string
  phoneNumber?: string
  profiles: UserProfile[]
  isSuperUser: boolean
  isGeneratorAdmin: boolean
  isSupportStaff: boolean
  status?: UserStatus
}

const parseResponse = (response: z.infer<typeof responseSchema>): User[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    email: item.email,
    phoneNumber: item.phone_number,
    profiles: getUserProfiles({
      isGeneratorAdmin: item.is_generator_admin,
      isSuperUser: item.is_superuser,
      isSupportStaff: item.is_support_staff,
      profiles: item.profiles
    }),
    isSuperUser: item.is_superuser,
    isGeneratorAdmin: item.is_generator_admin,
    isSupportStaff: item.is_support_staff,
    status: item.status
  }))

export type UserParams = {
  id?: string[]
  profile?: UserProfile[]
  email?: string[]
  phoneNumber?: string[]
  status?: UserStatus[]
} & PaginationParams

const useUsers = (params: UserParams = {}) =>
  useQuery({
    queryKey: ['userList', params],
    staleTime: 60 * 100,
    queryFn: async () => {
      const userList = await httpService.panel.panelUsersList({
        page: params.page,
        // @ts-expect-error Waits for backend fix
        page_size: params.pageSize,
        id: params.id,
        email: params.email,
        phone_number: params.phoneNumber,
        profile: params.profile,
        status: params.status,
        fetchKeys: {
          id: true,
          name: true,
          profiles: true,
          email: true,
          phone_number: true,
          status: true,
          is_superuser: true,
          is_generator_admin: true,
          is_support_staff: true
        }
      })

      const parsedUserList = parseResponse(
        responseSchema.parse(userList.results)
      )
      return {
        list: parsedUserList,
        count: userList.count
      }
    }
  })

export default useUsers
